import React, { useState, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useFlowButtons, useDeleteFlowButton } from '@/hooks/useFlowButtons';
import FlowButtonRenderer from './FlowButtonRenderer';
import FlowButtonCreator from './FlowButtonCreator';
import FlowButtonAddFAB from './FlowButtonAddFAB';
import Dialog, { DialogButton } from '@/components/ui/Dialog';
import { Trash2 } from 'lucide-react';
import type { IFlowButton } from '@/types/flowbutton';

const FlowButtonLayer: React.FC = () => {
  const location = useLocation();
  const currentPath = location.pathname;

  // Only show on /app routes
  if (!currentPath.startsWith('/app')) return null;

  return <FlowButtonLayerInner currentPath={currentPath} />;
};

const FlowButtonLayerInner: React.FC<{ currentPath: string }> = ({ currentPath }) => {
  const { data: buttons } = useFlowButtons({ page: currentPath });
  const deleteFlowButton = useDeleteFlowButton();
  const [showCreator, setShowCreator] = useState(false);
  const [editingButton, setEditingButton] = useState<IFlowButton | undefined>();
  const [draggingButton, setDraggingButton] = useState<IFlowButton | null>(null);
  const [fabHovering, setFabHovering] = useState(false);
  const [deleteConfirmButton, setDeleteConfirmButton] = useState<IFlowButton | null>(null);
  const fabRef = useRef<HTMLButtonElement>(null);

  const handleEdit = (button: IFlowButton) => {
    setEditingButton(button);
    setShowCreator(true);
  };

  const handleDelete = (id: string) => {
    const btn = buttons?.find(b => b.id === id);
    if (btn) {
      setDeleteConfirmButton(btn);
    }
  };

  const handleConfirmDelete = () => {
    if (deleteConfirmButton) {
      deleteFlowButton.mutate(deleteConfirmButton.id);
      // Clean up saved position
      localStorage.removeItem(`flowbutton-pos:${deleteConfirmButton.id}`);
      setDeleteConfirmButton(null);
    }
  };

  const handleCloseCreator = () => {
    setShowCreator(false);
    setEditingButton(undefined);
  };

  const isOverFab = useCallback((px: number, py: number): boolean => {
    if (!fabRef.current) return false;
    const rect = fabRef.current.getBoundingClientRect();
    // Generous hit area (20px padding)
    return (
      px >= rect.left - 20 &&
      px <= rect.right + 20 &&
      py >= rect.top - 20 &&
      py <= rect.bottom + 20
    );
  }, []);

  const handleDragStart = useCallback((button: IFlowButton) => {
    setDraggingButton(button);
    setFabHovering(false);
  }, []);

  const handleDragEnd = useCallback((button: IFlowButton, pointerX: number, pointerY: number) => {
    if (pointerX >= 0 && pointerY >= 0 && isOverFab(pointerX, pointerY)) {
      setDeleteConfirmButton(button);
      // Reset position so it snaps back to default stack if user cancels
      localStorage.removeItem(`flowbutton-pos:${button.id}`);
    }
    setDraggingButton(null);
    setFabHovering(false);
  }, [isOverFab]);

  // Track pointer move globally while dragging to detect FAB hover
  React.useEffect(() => {
    if (!draggingButton) return;
    const handler = (e: PointerEvent) => {
      setFabHovering(isOverFab(e.clientX, e.clientY));
    };
    window.addEventListener('pointermove', handler);
    return () => window.removeEventListener('pointermove', handler);
  }, [draggingButton, isOverFab]);

  return (
    <>
      {/* Dark overlay when dragging */}
      {draggingButton && (
        <div className="fixed inset-0 bg-black/50 z-[45] transition-opacity duration-200 pointer-events-none" />
      )}

      {/* Render flow buttons */}
      {buttons?.map((button, index) => (
        <div
          key={button.id}
          style={{
            position: 'fixed',
            bottom: `${80 + index * 52}px`,
            right: '16px',
            zIndex: 40,
          }}
        >
          <FlowButtonRenderer
            button={button}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            isDragMode={!!draggingButton}
          />
        </div>
      ))}

      {/* Add FAB / Drop zone */}
      <FlowButtonAddFAB
        ref={fabRef}
        onClick={() => setShowCreator(true)}
        isDropTarget={!!draggingButton}
        isHovering={fabHovering}
      />

      {/* Creator dialog */}
      {showCreator && (
        <FlowButtonCreator
          open={showCreator}
          onClose={handleCloseCreator}
          editButton={editingButton}
          defaultPage={currentPath}
        />
      )}

      {/* Delete confirmation dialog */}
      <Dialog
        isOpen={!!deleteConfirmButton}
        onClose={() => setDeleteConfirmButton(null)}
        title="Delete Flow Button"
        headerColor="red"
        maxWidth="sm"
        icon={<Trash2 className="w-5 h-5 text-white" />}
        footer={
          <>
            <DialogButton onClick={() => setDeleteConfirmButton(null)}>
              Cancel
            </DialogButton>
            <DialogButton variant="danger" onClick={handleConfirmDelete}>
              Delete
            </DialogButton>
          </>
        }
      >
        <p className="text-gray-700 dark:text-gray-300">
          Are you sure you want to delete <span className="font-semibold">&ldquo;{deleteConfirmButton?.name}&rdquo;</span>?
          This action cannot be undone.
        </p>
      </Dialog>
    </>
  );
};

export default FlowButtonLayer;
